"use client"

import { useEffect } from "react"

import { createReport404Data, type Report404Data } from "@/types/404-tracking"

export default function Client404Tracker() {
  useEffect(() => {
    // Only track 404s in production
    if (process.env.NEXT_PUBLIC_VERCEL_ENV !== "production") {
      return undefined
    }
    // Track image loading errors
    const handleImageError = (event: Event) => {
      const target = event.target as HTMLImageElement
      if (!target || target.tagName !== "IMG") return

      const src = target.src || target.getAttribute("src") || ""

      // Skip data URLs and external images
      if (
        src.startsWith("data:") ||
        src.startsWith("http://") ||
        src.startsWith("https://")
      ) {
        // Only track if it's our domain
        try {
          const url = new URL(src)
          if (url.hostname !== window.location.hostname) return
        } catch {
          // If URL parsing fails, it's likely a relative path, so continue
        }
      }

      // Track the 404 with type-safe payload
      const payload: Report404Data = createReport404Data({
        path: src.replace(window.location.origin, ""),
        pathname: src.replace(window.location.origin, ""),
        type: "client-asset-404",
        isStaticAsset: true,
        isPage: false,
        errorType: "image",
        referrer: document.referrer || "direct",
        userAgent: navigator.userAgent,
        currentPage: window.location.href,
      })

      fetch("/api/track-404", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).catch(() => {
        // Silently fail
      })
    }

    // Track script/stylesheet loading errors
    const handleResourceError = (event: Event) => {
      const target = event.target as HTMLLinkElement | HTMLScriptElement
      if (!target) return

      const { tagName } = target
      if (tagName !== "LINK" && tagName !== "SCRIPT") return

      const src = (target as any).href || (target as any).src || ""

      // Skip external resources
      if (src.startsWith("http://") || src.startsWith("https://")) {
        try {
          const url = new URL(src)
          if (url.hostname !== window.location.hostname) return
        } catch {
          return
        }
      }

      // Track the 404 with type-safe payload
      const payload: Report404Data = createReport404Data({
        path: src.replace(window.location.origin, ""),
        pathname: src.replace(window.location.origin, ""),
        type: "client-asset-404",
        isStaticAsset: true,
        isPage: false,
        errorType: tagName.toLowerCase(),
        referrer: document.referrer || "direct",
        userAgent: navigator.userAgent,
        currentPage: window.location.href,
      })

      fetch("/api/track-404", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).catch(() => {
        // Silently fail
      })
    }

    // Add error listeners
    window.addEventListener("error", handleImageError, true)
    window.addEventListener("error", handleResourceError, true)

    // Also monitor dynamically added images
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeName === "IMG") {
            const img = node as HTMLImageElement
            img.addEventListener("error", handleImageError)
          }
        })
      })
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    // Cleanup
    return () => {
      window.removeEventListener("error", handleImageError, true)
      window.removeEventListener("error", handleResourceError, true)
      observer.disconnect()
    }
  }, [])

  return null
}
